import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Cpu, Activity, Zap, Database, ShieldCheck, GaugeCircle, Plug,
  ChevronRight, Send, CheckCircle2, ArrowRight, Settings as Cog,
  CircleDot, Network as NetworkIcon, Terminal,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

// Phase 36.4 — Public landing page for the Industrial Adapter (SCADA / OPC UA / MODBUS)
// No auth required. Built to signal credibility for mining/shipping/energy CFO conversations
// and capture leads via the waitlist endpoint.

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;

const PROTOCOLS = [
  {
    name: "OPC UA",
    icon: NetworkIcon,
    color: "#FFB000",
    blurb: "Modern industrial standard. Secure, typed, subscribe-to-changes.",
    where: "Siemens · Rockwell · GE · ABB control PCs",
    examples: "Mine_Stockpile.Tonnes · Crusher_2.Output_TPH",
  },
  {
    name: "MODBUS",
    icon: CircleDot,
    color: "#00E676",
    blurb: "Battle-tested 1979 protocol. Still runs ~70% of factory floors.",
    where: "Pumps · flow meters · PLCs · serial-over-RS485 / TCP",
    examples: "Register 40010 → 412.7 (tonnes / hour)",
  },
  {
    name: "SCADA Historian",
    icon: Database,
    color: "#FF6B47",
    blurb: "The plant's existing time-series database. We tap straight in.",
    where: "Wonderware · Ignition · Honeywell PHD · OSIsoft PI",
    examples: "Plant_Power.kWh · Vessel_AIS.ETA",
  },
];

const PIPELINE = [
  { label: "PLC / Sensor", note: "Existing kit", color: "#52525b" },
  { label: "SCADA / OPC UA", note: "On-site", color: "#52525b" },
  { label: "DARA Edge Gateway", note: "Linux/Windows · Python", color: "#FFB000" },
  { label: "TLS push", note: "Buffered, auto-resume", color: "#FFB000" },
  { label: "/api/iot/ingest", note: "DARA cloud", color: "#00E676" },
  { label: "Hedge Co-Pilot · Forecasts · Alerts", note: "Live decisions", color: "#00E676" },
];

const USE_CASES = [
  {
    icon: Activity,
    title: "Live cash-flow forecasts",
    body: "Stockpile tonnes × today's iron-ore index = your real revenue band, refreshed every shift instead of every month-end.",
  },
  {
    icon: GaugeCircle,
    title: "Predictive maintenance alerts",
    body: "Conveyor vibration up 8% over 24h? DARA pings your Slack with a 7-day inspection window before the bearing seizes.",
  },
  {
    icon: Zap,
    title: "ESG disclosure pre-fill",
    body: "Plant kWh + Scope-2 emissions factors auto-feed your sustainability report. JORC, IFRS S2, AASB compliance baked in.",
  },
  {
    icon: ShieldCheck,
    title: "Hedge-Ledger drives off real volume",
    body: "Your accepted hedges mark-to-market against actual shipped tonnes — not last month's CSV. Treasury close in minutes.",
  },
];

export default function Industrial() {
  usePageview("/industrial");
  const [count, setCount] = useState(null);
  const [sample, setSample] = useState(null);
  const [ingestResult, setIngestResult] = useState(null);
  const [pinging, setPinging] = useState(false);

  // Waitlist form
  const [form, setForm] = useState({
    email: "", name: "", company: "", role: "", industry: "Mining",
    plant_count: "", protocols: [], notes: "",
    // Phase 25.1 — pilot-application qualifying fields
    pilot_application: false,
    decision_timeframe: "",
    pain_points: [],
    existing_stack_now: "",
    pilot_ready: null,
    decision_maker: "",
    budget_band: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/iot/waitlist/count`).then(r => r.json()).then(setCount).catch(() => {});
    fetch(`${BACKEND_URL}/api/iot/sample`).then(r => r.json()).then(setSample).catch(() => {});
  }, []);

  const tryIngest = async () => {
    if (!sample?.sample_payload) return;
    setPinging(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/ingest`, {
        method: "POST",
        headers: {
          "Authorization": "Bearer dara_demo_token",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sample.sample_payload),
      });
      const data = await r.json();
      setIngestResult(data);
      if (r.ok) toast.success("Telemetry accepted. ✅");
      else toast.error(data.detail || "Ingest failed");
    } catch (e) {
      toast.error("Network error");
    } finally {
      setPinging(false);
    }
  };

  const toggleProtocol = (p) => {
    setForm(f => ({
      ...f,
      protocols: f.protocols.includes(p)
        ? f.protocols.filter(x => x !== p)
        : [...f.protocols, p],
    }));
  };

  const togglePainPoint = (p) => {
    setForm(f => ({
      ...f,
      pain_points: f.pain_points.includes(p)
        ? f.pain_points.filter(x => x !== p)
        : [...f.pain_points, p],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.company) {
      toast.error("Please fill name, email, and company.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/iot/waitlist`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          plant_count: form.plant_count ? Number(form.plant_count) : null,
          // Booleans default to null when user hasn't touched them
          pilot_ready: form.pilot_ready === true ? true : (form.pilot_ready === false ? false : null),
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Submit failed");
      setSubmitted(data);
      toast.success(`You're on the list — position #${data.queue_position}`);
      // Refresh count
      fetch(`${BACKEND_URL}/api/iot/waitlist/count`).then(r => r.json()).then(setCount).catch(() => {});
    } catch (e) {
      toast.error(e.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="industrial-landing">
      {/* HEADER */}
      <header className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group" data-testid="industrial-back-home">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
          </Link>
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="industrial-login-link"
              className="font-mono text-[11px] uppercase tracking-widest text-zinc-400 hover:text-white px-3 py-2">
              LOGIN
            </Link>
            <a href="#waitlist" data-testid="industrial-cta-top"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-2 hover:opacity-90">
              JOIN WAITLIST
            </a>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="border-b border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 mb-4">
              <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000]">
                // INDUSTRIAL ADAPTER · Q3 2026 PREVIEW
              </span>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-600">
                COMING SOON
              </span>
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05]">
              Plug DARA into your<br />
              <span className="text-[#FFB000]">factory floor.</span>
            </h1>
            <p className="text-base sm:text-lg text-zinc-400 max-w-2xl mt-6 leading-relaxed">
              Read OPC UA, MODBUS and SCADA historians directly. DARA's Edge Gateway streams
              your shipped tonnes, plant power, vibration RMS and stockpile balances into the
              same intelligence layer that drives your Hedge Ledger and cash-flow forecasts —
              live, every shift, without buying a single new sensor.
            </p>

            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <a href="#waitlist" data-testid="industrial-cta-hero"
                className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 flex items-center gap-2">
                <Send className="w-3.5 h-3.5" /> JOIN PRIVATE BETA
              </a>
              <a href="#how-it-works"
                className="border border-zinc-800 hover:border-zinc-600 font-mono text-[11px] uppercase tracking-widest px-5 py-3 text-zinc-300 hover:text-white flex items-center gap-2">
                HOW IT WORKS <ArrowRight className="w-3 h-3" />
              </a>
            </div>

            {count !== null && (
              <div className="mt-6 flex items-center gap-3 font-mono text-[11px] text-zinc-500" data-testid="industrial-waitlist-count">
                <span className="w-2 h-2 rounded-full bg-[#00E676] animate-pulse" />
                <span>{count.displayed} mining & shipping operators on the waitlist</span>
              </div>
            )}
          </div>

          {/* Hero ASCII / pipeline diagram */}
          <div className="lg:col-span-5">
            <div className="border border-zinc-900 bg-zinc-950/40 p-5 font-mono text-[11px] leading-relaxed">
              <div className="text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
                // FLOW · SITE → DARA
              </div>
              <pre className="text-zinc-400 whitespace-pre overflow-x-auto" style={{ fontSize: 10 }}>
{`[Pilbara mine site]                        [DARA cloud]

  PLCs / sensors                            
       ↓                                    
  SCADA historian ─┐                        
                   │                        
  OPC UA server ───┤                        
                   │                        
  MODBUS RTU ──────┴─▶ DARA Edge ─▶ TLS ─▶ /api/iot/ingest
                       Gateway              ↓
                       (Python)             ↓
                                           Hedge Co-Pilot
                                           Forecasts
                                           Slack alerts`}
              </pre>
            </div>

            {/* Live demo */}
            <div className="border border-zinc-900 bg-zinc-950/40 p-5 mt-3">
              <div className="text-[10px] uppercase tracking-widest text-[#FFB000] mb-2 flex items-center gap-1.5">
                <Plug className="w-3 h-3" /> LIVE INGEST DEMO
              </div>
              <p className="text-xs text-zinc-500 leading-relaxed mb-3">
                Try the production endpoint — DARA accepts a sample payload and echoes back the receipt.
              </p>
              <button onClick={tryIngest} disabled={pinging || !sample}
                data-testid="industrial-try-ingest"
                className="w-full bg-zinc-900 hover:bg-zinc-800 border border-zinc-800 hover:border-[#FFB000] disabled:opacity-50 px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-300 hover:text-[#FFB000] flex items-center justify-center gap-2">
                {pinging ? "POSTING…" : "POST SAMPLE PAYLOAD"} <ArrowRight className="w-3 h-3" />
              </button>
              {ingestResult && (
                <pre className="mt-3 text-[10px] text-[#00E676] bg-black p-2 border border-zinc-900 overflow-x-auto" data-testid="industrial-ingest-result">
{JSON.stringify(ingestResult, null, 2)}
                </pre>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* PROTOCOLS */}
      <section id="how-it-works" className="border-b border-zinc-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            // PROTOCOLS WE READ
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Three industrial languages. One unified data layer.
          </h2>
          <p className="text-zinc-400 max-w-2xl mt-3 text-base leading-relaxed">
            Your plant already speaks at least one of these. Our gateway translates them all
            into clean JSON DARA can analyse alongside your CSVs, Xero books, and commodity
            indices — no new hardware, no rip-and-replace.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 mt-10 border border-zinc-900">
            {PROTOCOLS.map((p, i) => {
              const Icon = p.icon;
              return (
                <div key={p.name} className="bg-black p-6" data-testid={`industrial-protocol-${i + 1}`}>
                  <Icon className="w-6 h-6 mb-4" style={{ color: p.color }} />
                  <div className="font-heading font-black text-2xl tracking-tight">{p.name}</div>
                  <p className="text-sm text-zinc-400 mt-2 leading-relaxed">{p.blurb}</p>
                  <div className="mt-4 space-y-1">
                    <div>
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">FOUND ON</span>
                      <div className="font-mono text-[11px] text-zinc-300 mt-0.5">{p.where}</div>
                    </div>
                    <div className="pt-2">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">EXAMPLE TAGS</span>
                      <div className="font-mono text-[11px] text-zinc-400 mt-0.5">{p.examples}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PIPELINE */}
      <section className="border-b border-zinc-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            // PIPELINE
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Site → DARA in 6 steps.
          </h2>

          <div className="mt-10 grid grid-cols-1 lg:grid-cols-6 gap-3">
            {PIPELINE.map((p, i) => (
              <div key={i} className="border border-zinc-900 bg-zinc-950/40 p-4" data-testid={`industrial-pipeline-${i + 1}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-mono text-[9px] uppercase tracking-widest" style={{ color: p.color }}>
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <ChevronRight className="w-3 h-3" style={{ color: p.color }} />
                </div>
                <div className="font-heading font-bold text-sm tracking-tight leading-snug">{p.label}</div>
                <div className="font-mono text-[10px] text-zinc-500 mt-1">{p.note}</div>
              </div>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
            {[
              { label: "ZERO NEW HARDWARE", body: "Runs on the Windows PC or Linux box you already have in the control room. Or a Raspberry Pi if you prefer." },
              { label: "BUFFERED + RESUMABLE", body: "Edge gateway buffers locally so a 30-min internet outage doesn't lose data. Auto-resumes on reconnect." },
              { label: "READ-ONLY BY DESIGN", body: "DARA never writes back to your PLCs. We only listen — your operations team stays in full control." },
            ].map((c, i) => (
              <div key={i} className="border-l-2 border-[#FFB000] pl-4 py-2">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#FFB000]">{c.label}</div>
                <p className="text-sm text-zinc-400 mt-1 leading-relaxed">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* USE CASES */}
      <section className="border-b border-zinc-900 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500 mb-3">
            // WHAT IT UNLOCKS
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight max-w-2xl">
            Real telemetry. Real decisions.
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-zinc-900 mt-10 border border-zinc-900">
            {USE_CASES.map((u, i) => {
              const Icon = u.icon;
              return (
                <div key={i} className="bg-black p-6 flex gap-4">
                  <div className="flex-shrink-0">
                    <Icon className="w-5 h-5 text-[#FFB000]" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-base tracking-tight">{u.title}</div>
                    <p className="text-sm text-zinc-400 mt-1.5 leading-relaxed">{u.body}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ROI CALCULATOR (Phase 25.1e) — interactive widget that pulls qualified prospects into the waitlist */}
      <ROICalculatorSection
        onApplyToPilot={(presetIndustry, presetVolume, presetPlants) => {
          setForm(f => ({
            ...f,
            industry: presetIndustry || f.industry,
            plant_count: presetPlants ? String(presetPlants) : f.plant_count,
            pilot_application: true,
          }));
          // Scroll to the waitlist
          setTimeout(() => {
            const el = document.getElementById("waitlist");
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
          }, 100);
        }}
      />

      {/* WAITLIST */}
      <section id="waitlist" className="border-b border-zinc-900 py-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // PRIVATE BETA · Q3 2026
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            Join the waitlist.
          </h2>
          <p className="text-zinc-400 mt-3 max-w-xl text-base leading-relaxed">
            Limited to 25 mining, shipping, and energy operators for the first cohort. We'll
            help you install the gateway, map your tags, and prove out the business case
            against your existing data within two weeks.
          </p>

          {!submitted && (
            <form onSubmit={submit} className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-zinc-900 bg-zinc-950/40 p-6" data-testid="industrial-waitlist-form">
              <Input label="NAME *" value={form.name} onChange={v => setForm(f => ({ ...f, name: v }))} testid="waitlist-name" />
              <Input label="EMAIL *" type="email" value={form.email} onChange={v => setForm(f => ({ ...f, email: v }))} testid="waitlist-email" />
              <Input label="COMPANY *" value={form.company} onChange={v => setForm(f => ({ ...f, company: v }))} testid="waitlist-company" />
              <Input label="ROLE / TITLE" value={form.role} onChange={v => setForm(f => ({ ...f, role: v }))} placeholder="CFO · Head of Treasury · COO" testid="waitlist-role" />
              <Input label="PLANT / SITE COUNT" type="number" value={form.plant_count} onChange={v => setForm(f => ({ ...f, plant_count: v }))} placeholder="e.g. 4" testid="waitlist-plants" />
              <Select label="INDUSTRY" value={form.industry} onChange={v => setForm(f => ({ ...f, industry: v }))} testid="waitlist-industry"
                options={["Mining", "Shipping", "Heavy Machinery", "Oil & Gas", "Manufacturing", "Energy", "Other"]} />
              <div className="sm:col-span-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-2">PROTOCOLS YOU USE</span>
                <div className="flex gap-2 flex-wrap">
                  {["OPC UA", "MODBUS", "SCADA", "AWS IoT", "Azure IoT", "Other / Not sure"].map(p => (
                    <button type="button" key={p} onClick={() => toggleProtocol(p)}
                      data-testid={`waitlist-protocol-${p.toLowerCase().replace(/[^a-z]/g, "-")}`}
                      className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
                        form.protocols.includes(p)
                          ? "bg-[#FFB000]/10 border-[#FFB000] text-[#FFB000]"
                          : "border-zinc-800 text-zinc-400 hover:text-white"
                      }`}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2">
                <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-1">NOTES (OPTIONAL)</span>
                <textarea
                  data-testid="waitlist-notes"
                  value={form.notes}
                  onChange={(e) => setForm(f => ({ ...f, notes: e.target.value }))}
                  rows={3}
                  placeholder="What would you most want DARA to do with your live plant data?"
                  className="w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000] resize-none" />
              </div>

              {/* Phase 25.1 — Apply for 30-day Pilot toggle */}
              <div className="sm:col-span-2 border-t border-zinc-900 pt-5 mt-1">
                <label className="flex items-start gap-3 cursor-pointer group" data-testid="apply-pilot-toggle">
                  <input
                    type="checkbox"
                    checked={form.pilot_application}
                    onChange={(e) => setForm(f => ({ ...f, pilot_application: e.target.checked }))}
                    data-testid="pilot-checkbox"
                    className="mt-1 accent-[#FFB000] w-4 h-4 cursor-pointer"
                  />
                  <div>
                    <div className="font-mono text-[11px] uppercase tracking-widest text-[#FFB000] font-bold">
                      Apply for the 30-day pilot programme
                    </div>
                    <div className="font-mono text-[10px] text-zinc-500 mt-1 leading-relaxed">
                      Limited to 25 operators. We'll install the gateway, map your tags, and prove out the
                      business case against your existing data — at no cost — within two weeks. Helps us
                      prioritise.
                    </div>
                  </div>
                </label>
              </div>

              {form.pilot_application && (
                <>
                  <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 border border-[#FFB000]/30 bg-[#FFB000]/[0.02] p-4">
                    <Select
                      label="DECISION TIMEFRAME"
                      value={form.decision_timeframe}
                      onChange={v => setForm(f => ({ ...f, decision_timeframe: v }))}
                      testid="pilot-timeframe"
                      options={["", "this_quarter", "this_year", "exploring"]}
                    />
                    <Select
                      label="ARE YOU THE DECISION MAKER?"
                      value={form.decision_maker}
                      onChange={v => setForm(f => ({ ...f, decision_maker: v }))}
                      testid="pilot-decision-maker"
                      options={["", "yes", "influencer", "no"]}
                    />
                    <Select
                      label="DO YOU HAVE OPC UA / MODBUS / SCADA RUNNING TODAY?"
                      value={form.existing_stack_now}
                      onChange={v => setForm(f => ({ ...f, existing_stack_now: v }))}
                      testid="pilot-existing-stack"
                      options={["", "yes", "partial", "no"]}
                    />
                    <Select
                      label="BUDGET BAND (USD/YR)"
                      value={form.budget_band}
                      onChange={v => setForm(f => ({ ...f, budget_band: v }))}
                      testid="pilot-budget"
                      options={["", "<10k", "10-50k", "50-250k", "250k+", "tbd"]}
                    />
                    <div className="sm:col-span-2">
                      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-2">PRIMARY PAIN POINTS (PICK UP TO 3)</span>
                      <div className="flex gap-2 flex-wrap">
                        {[
                          ["forecast_accuracy", "Tonnes/shift forecast accuracy"],
                          ["predictive_maintenance", "Predictive maintenance"],
                          ["esg_reporting", "ESG / Scope-2 disclosure"],
                          ["hedge_volume", "Volume-driven hedge accuracy"],
                          ["downtime_cost", "Unplanned downtime cost"],
                          ["other", "Other / talk to us"],
                        ].map(([key, label]) => (
                          <button
                            type="button"
                            key={key}
                            onClick={() => togglePainPoint(key)}
                            data-testid={`pilot-pain-${key}`}
                            className={`px-3 py-1.5 font-mono text-[10px] uppercase tracking-widest border ${
                              form.pain_points.includes(key)
                                ? "bg-[#FFB000]/10 border-[#FFB000] text-[#FFB000]"
                                : "border-zinc-800 text-zinc-400 hover:text-white"
                            }`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.pilot_ready === true}
                          onChange={(e) => setForm(f => ({ ...f, pilot_ready: e.target.checked }))}
                          data-testid="pilot-ready-checkbox"
                          className="mt-1 accent-[#FFB000] w-4 h-4 cursor-pointer"
                        />
                        <span className="font-mono text-[10px] text-zinc-300 leading-relaxed">
                          Yes, we can commit to a 30-day pilot starting this quarter (1-2 hrs/week of our team's time, no $ commitment).
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}
              <div className="sm:col-span-2 flex justify-end">
                <button type="submit" disabled={submitting}
                  data-testid="waitlist-submit"
                  className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-6 py-3 hover:opacity-90 disabled:opacity-50 flex items-center gap-2">
                  {submitting ? "SUBMITTING…" : <><Send className="w-3.5 h-3.5" /> {form.pilot_application ? "APPLY FOR PILOT" : "JOIN WAITLIST"}</>}
                </button>
              </div>
            </form>
          )}

          {submitted && (
            <div className="mt-8 border border-[#00E676]/40 bg-[#00E676]/5 p-6 flex items-start gap-4" data-testid="industrial-waitlist-success">
              <CheckCircle2 className="w-6 h-6 text-[#00E676] mt-0.5 flex-shrink-0" />
              <div>
                <div className="font-heading font-black text-xl tracking-tight">You're in.</div>
                <p className="text-sm text-zinc-300 mt-1.5 leading-relaxed">
                  Queue position <span className="text-[#FFB000] font-bold">#{submitted.queue_position}</span>.
                  We'll reach out within a week to schedule a 30-minute scoping call. In the meantime,
                  if you want a working DARA workspace today, you can {" "}
                  <Link to="/signup" className="text-[#FFB000] underline">sign up</Link> and start
                  with CSV imports — the industrial adapter slots in once it's ready.
                </p>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between flex-wrap gap-4">
          <div className="font-mono text-[10px] text-zinc-600">
            DARA OS — Enterprise data-to-revenue terminal · daraos.ai
          </div>
          <div className="flex items-center gap-4">
            <Link to="/" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
              HOME
            </Link>
            <Link to="/login" className="font-mono text-[10px] uppercase tracking-widest text-zinc-500 hover:text-white">
              LOGIN
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Input({ label, value, onChange, type = "text", placeholder = "", testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testid}
        className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      />
    </label>
  );
}

function Select({ label, value, onChange, options, testid }) {
  return (
    <label className="block">
      <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2 font-mono text-xs text-white focus:outline-none focus:border-[#FFB000]"
      >
        {options.map(o => (<option key={o} value={o}>{o}</option>))}
      </select>
    </label>
  );
}


/* ────────────────────────────────────────────────────────────────────────
   ROI Calculator (Phase 25.1e)
   Live widget that lets prospects plug in production assumptions and see
   DARA's projected $ impact. Submitting "Email me this" pre-fills the
   waitlist with industry + plant_count and toggles pilot_application=true.
   ──────────────────────────────────────────────────────────────────────── */
const ROI_PRESETS = {
  Mining: {
    label: "Mining (iron ore / coal / gold)",
    volume: 8_000_000,        // tonnes / year
    volumeLabel: "tonnes / yr",
    price: 105,               // $ / tonne
    priceLabel: "$ / tonne",
    plants: 3,
    downtime: 480,            // hrs / yr
    downtimeCost: 18_000,     // $ / hr
    hedgeRatio: 35,           // % of production already hedged
    forecastDeltaPct: 12,     // baseline forecast error reducible by DARA
  },
  Shipping: {
    label: "Shipping (bulk / container / tanker)",
    volume: 2_400_000,        // tonnes-NM / year proxy
    volumeLabel: "voyage-tonnes / yr",
    price: 28,
    priceLabel: "$ / tonne-voyage",
    plants: 5,
    downtime: 240,
    downtimeCost: 35_000,
    hedgeRatio: 20,
    forecastDeltaPct: 18,
  },
  "Heavy Machinery": {
    label: "Heavy Machinery / OEM",
    volume: 4_800,            // units / year
    volumeLabel: "units / yr",
    price: 280_000,
    priceLabel: "$ / unit",
    plants: 2,
    downtime: 320,
    downtimeCost: 12_000,
    hedgeRatio: 10,
    forecastDeltaPct: 9,
  },
  Manufacturing: {
    label: "Manufacturing (food / chem / steel)",
    volume: 320_000,
    volumeLabel: "tonnes / yr",
    price: 850,
    priceLabel: "$ / tonne",
    plants: 4,
    downtime: 600,
    downtimeCost: 22_000,
    hedgeRatio: 25,
    forecastDeltaPct: 14,
  },
};

// Conservative DARA improvement assumptions for V1 calculator.
const DARA_FORECAST_IMPROVEMENT_PCT = 0.45;       // closes ~45% of the forecast-error gap
const DARA_DOWNTIME_REDUCTION_PCT = 0.20;         // 20% reduction in unplanned downtime
const DARA_HEDGE_LIFT_PCT = 0.15;                 // 15% lift in hedge-program effectiveness
const DARA_ANNUAL_PRICE = 60_000;                 // $/yr — used to compute payback months

function fmt$(n) {
  if (!Number.isFinite(n)) return "—";
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${Math.round(n).toLocaleString()}`;
}

function fmtN(n, suffix = "") {
  if (!Number.isFinite(n)) return "—";
  return `${Math.round(n).toLocaleString()}${suffix}`;
}

function ROICalculatorSection({ onApplyToPilot }) {
  const [industry, setIndustry] = useState("Mining");
  const preset = ROI_PRESETS[industry];
  const [volume, setVolume] = useState(preset.volume);
  const [price, setPrice] = useState(preset.price);
  const [plants, setPlants] = useState(preset.plants);
  const [downtime, setDowntime] = useState(preset.downtime);
  const [downtimeCost, setDowntimeCost] = useState(preset.downtimeCost);
  const [hedgeRatio, setHedgeRatio] = useState(preset.hedgeRatio);
  // Phase 25.1f — anonymous session ID for ROI calculator engagement tracking.
  // Persisted in sessionStorage so reloading the page doesn't double-count
  // but a fresh tab does count as a new session.
  const sessionIdRef = React.useRef(
    (typeof sessionStorage !== "undefined" && sessionStorage.getItem("dara_roi_session_id"))
      || (Math.random().toString(36).slice(2) + Date.now().toString(36))
  );
  React.useEffect(() => {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("dara_roi_session_id", sessionIdRef.current);
    }
  }, []);

  // Reset to preset when industry changes
  useEffect(() => {
    const p = ROI_PRESETS[industry];
    setVolume(p.volume);
    setPrice(p.price);
    setPlants(p.plants);
    setDowntime(p.downtime);
    setDowntimeCost(p.downtimeCost);
    setHedgeRatio(p.hedgeRatio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry]);

  // Computed outputs
  const annualRevenue = volume * price;
  const unhedgedRevenue = annualRevenue * (1 - hedgeRatio / 100);
  // DARA forecast accuracy lift translates baseline forecast-error into recovered margin
  const forecastImpact =
    annualRevenue *
    (preset.forecastDeltaPct / 100) *
    DARA_FORECAST_IMPROVEMENT_PCT;
  // Downtime: 20% reduction × hours × cost
  const downtimeImpact = downtime * downtimeCost * DARA_DOWNTIME_REDUCTION_PCT;
  // Hedge effectiveness uplift on the unhedged tail
  const hedgeImpact = unhedgedRevenue * 0.06 * DARA_HEDGE_LIFT_PCT;
  const totalAnnualImpact = forecastImpact + downtimeImpact + hedgeImpact;
  const paybackMonths =
    totalAnnualImpact > 0
      ? (DARA_ANNUAL_PRICE / totalAnnualImpact) * 12
      : null;
  const roiX = totalAnnualImpact / DARA_ANNUAL_PRICE;

  // Phase 25.1f — debounced session log. Fires 2.5s after the last input change.
  // Skips the very first render (no point logging a session at default values).
  const firstRenderRef = React.useRef(true);
  React.useEffect(() => {
    if (firstRenderRef.current) {
      firstRenderRef.current = false;
      return;
    }
    const t = setTimeout(() => {
      try {
        fetch(`${BACKEND_URL}/api/iot/roi-session`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            session_id: sessionIdRef.current,
            industry,
            volume, price, plants,
            downtime, downtime_cost: downtimeCost, hedge_ratio: hedgeRatio,
            annual_revenue: annualRevenue,
            forecast_impact: forecastImpact,
            downtime_impact: downtimeImpact,
            hedge_impact: hedgeImpact,
            total_annual_impact: totalAnnualImpact,
            roi_multiple: Number.isFinite(roiX) ? roiX : null,
            payback_months: Number.isFinite(paybackMonths) ? paybackMonths : null,
            converted_to_pilot: false,
          }),
        }).catch(() => {});
      } catch { /* swallow — telemetry is best-effort */ }
    }, 2500);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [industry, volume, price, plants, downtime, downtimeCost, hedgeRatio]);

  const handleApplyToPilot = () => {
    // Mark this session as converted before scrolling to the form
    try {
      fetch(`${BACKEND_URL}/api/iot/roi-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionIdRef.current,
          industry,
          volume, price, plants,
          downtime, downtime_cost: downtimeCost, hedge_ratio: hedgeRatio,
          annual_revenue: annualRevenue,
          forecast_impact: forecastImpact,
          downtime_impact: downtimeImpact,
          hedge_impact: hedgeImpact,
          total_annual_impact: totalAnnualImpact,
          roi_multiple: Number.isFinite(roiX) ? roiX : null,
          payback_months: Number.isFinite(paybackMonths) ? paybackMonths : null,
          converted_to_pilot: true,
        }),
      }).catch(() => {});
    } catch { /* swallow */ }
    onApplyToPilot(industry, volume, plants);
  };

  return (
    <section
      id="roi-calculator"
      className="border-b border-zinc-900 py-20 bg-gradient-to-b from-black to-zinc-950/50"
      data-testid="roi-calculator-section"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="mb-8 text-center">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#00E676] mb-3">
            // ROI CALCULATOR · LIVE
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl tracking-tight">
            What's DARA worth to your operation?
          </h2>
          <p className="text-zinc-400 mt-3 max-w-xl mx-auto text-sm leading-relaxed">
            Plug in your production assumptions. Numbers are conservative —
            we'd rather under-promise. Real pilot results often exceed these.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* INPUTS */}
          <div className="border border-zinc-900 bg-zinc-950/60 p-5 sm:p-6 space-y-5">
            <div>
              <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500 block mb-2">
                INDUSTRY
              </span>
              <div className="grid grid-cols-2 gap-2">
                {Object.keys(ROI_PRESETS).map((k) => (
                  <button
                    key={k}
                    onClick={() => setIndustry(k)}
                    data-testid={`roi-industry-${k.toLowerCase().replace(/\s/g, "-")}`}
                    className={`px-3 py-2.5 font-mono text-[10px] uppercase tracking-widest border text-left ${
                      industry === k
                        ? "bg-[#FFB000]/10 border-[#FFB000] text-[#FFB000]"
                        : "border-zinc-800 text-zinc-400 hover:text-white"
                    }`}
                  >
                    {k}
                  </button>
                ))}
              </div>
            </div>

            <NumberSliderField
              label={`ANNUAL VOLUME (${preset.volumeLabel})`}
              value={volume}
              onChange={setVolume}
              min={preset.volume / 5}
              max={preset.volume * 4}
              step={Math.max(1, Math.round(preset.volume / 100))}
              testid="roi-input-volume"
            />
            <NumberSliderField
              label={`AVG PRICE (${preset.priceLabel})`}
              value={price}
              onChange={setPrice}
              min={Math.max(1, preset.price * 0.3)}
              max={preset.price * 2.5}
              step={Math.max(1, Math.round(preset.price / 100))}
              testid="roi-input-price"
            />
            <NumberSliderField
              label="PLANT / SITE COUNT"
              value={plants}
              onChange={setPlants}
              min={1}
              max={50}
              step={1}
              testid="roi-input-plants"
            />
            <NumberSliderField
              label="UNPLANNED DOWNTIME (HRS / YR)"
              value={downtime}
              onChange={setDowntime}
              min={0}
              max={2000}
              step={10}
              testid="roi-input-downtime"
            />
            <NumberSliderField
              label="DOWNTIME COST ($ / HR)"
              value={downtimeCost}
              onChange={setDowntimeCost}
              min={500}
              max={150_000}
              step={500}
              testid="roi-input-downtime-cost"
            />
            <NumberSliderField
              label={`HEDGE RATIO (% currently hedged)`}
              value={hedgeRatio}
              onChange={setHedgeRatio}
              min={0}
              max={100}
              step={5}
              testid="roi-input-hedge"
              suffix="%"
            />
          </div>

          {/* OUTPUTS */}
          <div className="border border-[#00E676]/30 bg-gradient-to-br from-[#00E676]/[0.04] to-zinc-950/60 p-5 sm:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="font-mono text-[10px] uppercase tracking-widest text-[#00E676]">
                  // PROJECTED ANNUAL IMPACT
                </div>
                <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-600 mt-1">
                  for {preset.label}
                </div>
              </div>
              <div className="px-2 py-0.5 border border-[#00E676]/40 text-[#00E676] font-mono text-[9px] uppercase tracking-widest">
                ● LIVE
              </div>
            </div>

            <div className="space-y-4">
              <KPIRow
                label="Annual revenue baseline"
                value={fmt$(annualRevenue)}
                muted
                testid="roi-out-revenue"
              />
              <KPIRow
                label="Forecast accuracy lift"
                sub="Closes ~45% of your forecast-error gap"
                value={fmt$(forecastImpact)}
                testid="roi-out-forecast"
              />
              <KPIRow
                label="Downtime reduction"
                sub="~20% fewer unplanned hours"
                value={fmt$(downtimeImpact)}
                testid="roi-out-downtime"
              />
              <KPIRow
                label="Hedge effectiveness uplift"
                sub="On the unhedged tail of revenue"
                value={fmt$(hedgeImpact)}
                testid="roi-out-hedge"
              />

              <div className="border-t border-[#00E676]/30 pt-4">
                <KPIRow
                  label="Total annual $ impact"
                  value={fmt$(totalAnnualImpact)}
                  big
                  highlight
                  testid="roi-out-total"
                />
                <div className="grid grid-cols-2 gap-3 mt-4">
                  <div className="border border-zinc-900 bg-black/40 p-3">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                      ROI multiple
                    </div>
                    <div
                      className="font-heading font-black text-2xl text-[#FFB000] mt-1 tracking-tighter"
                      data-testid="roi-out-multiple"
                    >
                      {Number.isFinite(roiX) && roiX > 0 ? `${roiX.toFixed(1)}×` : "—"}
                    </div>
                  </div>
                  <div className="border border-zinc-900 bg-black/40 p-3">
                    <div className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                      Payback
                    </div>
                    <div
                      className="font-heading font-black text-2xl text-[#FFB000] mt-1 tracking-tighter"
                      data-testid="roi-out-payback"
                    >
                      {paybackMonths && Number.isFinite(paybackMonths)
                        ? paybackMonths < 12
                          ? `${paybackMonths.toFixed(1)} mo`
                          : `${(paybackMonths / 12).toFixed(1)} yr`
                        : "—"}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={handleApplyToPilot}
              data-testid="roi-cta-pilot"
              className="mt-6 w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-4 py-3 hover:opacity-90 inline-flex items-center justify-center gap-2"
            >
              GET THIS REPORT EMAILED → APPLY FOR PILOT
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <div className="font-mono text-[10px] text-zinc-600 mt-3 text-center leading-relaxed">
              Submit the form below and we'll send you a tailored ROI breakdown
              for {preset.label.toLowerCase()} within 24 hours.
            </div>
          </div>
        </div>

        <div className="mt-8 font-mono text-[9px] text-zinc-700 max-w-3xl mx-auto leading-relaxed">
          <strong className="text-zinc-500">Methodology:</strong> Annual revenue
          = volume × price. DARA forecast lift = baseline forecast error band ×
          45% closure. Downtime lift = hours × cost/hr × 20%. Hedge lift = unhedged
          revenue × 6% volatility band × 15% effectiveness gain. Pricing assumes
          DARA at $60K/yr per workspace; multi-site discounts available.
        </div>
      </div>
    </section>
  );
}

function NumberSliderField({ label, value, onChange, min, max, step, testid, suffix = "" }) {
  return (
    <div>
      <div className="flex items-baseline justify-between mb-1.5">
        <span className="font-mono text-[9px] uppercase tracking-widest text-zinc-500">{label}</span>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value) || 0)}
          data-testid={`${testid}-num`}
          className="w-32 bg-black border border-zinc-800 px-2 py-1 font-mono text-[11px] text-[#FFB000] text-right focus:outline-none focus:border-[#FFB000]"
        />
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full accent-[#FFB000] cursor-pointer"
      />
      <div className="flex justify-between font-mono text-[9px] text-zinc-700 mt-0.5">
        <span>{fmtN(min, suffix)}</span>
        <span>{fmtN(max, suffix)}</span>
      </div>
    </div>
  );
}

function KPIRow({ label, sub, value, muted, big, highlight, testid }) {
  return (
    <div className="flex items-baseline justify-between gap-3" data-testid={testid}>
      <div>
        <div className={`font-mono text-[10px] uppercase tracking-widest ${muted ? "text-zinc-600" : "text-zinc-400"}`}>
          {label}
        </div>
        {sub && <div className="font-mono text-[9px] text-zinc-700 mt-0.5">{sub}</div>}
      </div>
      <div
        className={`font-heading ${big ? "text-3xl" : "text-lg"} font-black tracking-tighter ${
          highlight ? "text-[#00E676]" : muted ? "text-zinc-500" : "text-white"
        }`}
      >
        {value}
      </div>
    </div>
  );
}
